import React, { useRef, useState } from "react";
import { useEffect } from "react";
import "./EditObjectModal.css";
import { useContext } from "react";
import { UserContext } from "../../../context/UserContext";
import { Modal } from "../ModalComponent/Modal";
import ModalDividerLine from "../ModalComponent/modal-divider-line";
import { DELETE, GET, PATCH, POST, PUT } from "../../../utils/httpUtils";
import { Alert, BytesToGb, BytesToMb, DeepCopy, fallbackCopy, GetLocalSetting, GetObjectToken, Log, Try, wait } from "../../../utils/genericUtils";
import trashIcon from "../../../assets/images/trashIcon.png";
import { ConfirmationModal } from "../ConfirmationModal/confirmation-modal";
import { LoaderContext } from "../../../context/LoaderContext";
// icons
import wanrningIcon from "../../../assets/images/warning.png";
import copy from "../../../assets/images/copy.png";
import { API_URL } from "../../../environmentVariables";
import { AlertContext } from "../../../context/AlertContext";
import { NotificationsContext } from "../../../context/NotificationsContext";
const defaultProps = {
  id: "id",
  isPublicView: false,
  isActive: false,
  header: "header",
  message: "message",
  headerFontSize: 25,
  messageFontSize: 15,
  icon: undefined,
  iconLeft: false,
  textAlign: "left",
  showDivider: true,
  animate: true,
  animationTime: 300,
  bg_opacity: 0.5,
  height: undefined,
  width: undefined,
  children: null,
  objectName: "",
  directory: "",
  isFolder: false,
  DateCreated: new Date(),
  DateModified: new Date(),
  bytes: 0,
  isTrashBin: false,
  onClickOut: () => {},
  onCloseModal: () => {},
  forceUpdate: () => {},
};

function EditObjectModal(props = defaultProps) {
  const { user, loginUser } = useContext(UserContext);
  const { show, showloader, hideloader } = useContext(LoaderContext);
  const { globalAlert } = useContext(AlertContext);
  const { pushNotification } = useContext(NotificationsContext);

  const [trashModalActive, setTrashModalActive] = useState(false);
  const [restoreModalActive, setRestoreModalActive] = useState(false);
  const [settingIsLocked, setSettingIsLocked] = useState(false);

  const [passwordFieldsValid, setPasswordFieldsValid] = useState(false);

  const [passwordValidation, setPasswordValidation] = useState(null);
  const [confirmPasswordValidation, setConfirmPasswordValidation] = useState(null);

  const [name, setName] = useState(props.name);

  //input refs:
  const name_input = useRef();
  const base_dir_input = useRef();
  const isPublicInput = useRef();
  const isHiddenInput = useRef();
  const isLockedInput = useRef();
  const passwordInput = useRef();
  const confirmPasswordInput = useRef();

  //constants
  const showDividerLine = true;
  const modal_type = props.isFolder ? "Folder" : "File";

  //special logic to keep quick rename and modal rename in sync
  useEffect(()=>{
    if(name !== props.name) setName(props.name);
  },[props.name])

  function base(base_dir) {
    if (base_dir === undefined) return "";
    return base_dir === "/" ? "" : base_dir;
  }

  function RenderPossibleDirectories() {
    Log("props.possibleDirectories: ", props.possibleDirectories);

    //remove invalid options
    //ex cant move /videos to /videos/something
    const formatDir = (d) => (d.baseDirectory === "/" ? "/" + d.objectName : d.baseDirectory + "/" + d.objectName);

    let valid_dirs = props.possibleDirectories.filter((x) => {
      let stringa = formatDir(x);
      let stringb = formatDir({ baseDirectory: props.directory, objectName: props.objectName });
      //do not allow them to move to trash via this move object function
      let parts = stringa.split("/");
      return !stringa.includes(stringb) && parts[1] !== "Trash";
    });

    //sort the dirs alphabetically and by depth
    valid_dirs.sort((a, b) => {
      let _a = formatDir(a) ?? a.fullPath;
      let _b = formatDir(b) ?? b.fullPath;

      const depthA = _a.split("/").filter(Boolean).length;
      const depthB = _b.split("/").filter(Boolean).length;

      if (depthA === depthB) {
        return _a.localeCompare(_b);
      }

      return depthA - depthB;
    });

    //append root
    valid_dirs = ["/", ...valid_dirs];

    return valid_dirs.map((dir, index) => {
      let option_value = dir.fullPath !== undefined ? dir.fullPath : base(dir.baseDirectory) + "/" + (dir.objectName ?? "");

      //only render if the option value is set
      if (option_value) {
        return (
          <option key={props.id + "-possible-dir-" + index} value={option_value}>
            {option_value}
          </option>
        );
      }
    });
  }

  function formatBytes(bytes) {
    let mb = BytesToMb(bytes);
    if (mb >= 1000) return BytesToGb(bytes).toFixed(2) + "GB";
    else return mb.toFixed(2) + "MB";
  }

  function OnTrashClicked() {
    if (!props.isTrashBin) {
      //check if they set not to show again
      let dontAsk = GetLocalSetting("ask_move_to_trash") === false;
      Log("dont ask: ", dontAsk);
      if (dontAsk === false) setTrashModalActive(true);
      else MoveToTrash();
    } else {
      //check if they set not to show again
      let dontAsk = GetLocalSetting("ask_permenantly_delete") === false;
      Log("dont ask: ", dontAsk);
      if (dontAsk === false) setTrashModalActive(true);
      else DeleteObjectPermenantly();
    }
  }

  function OnRestoreClicked() {
    //check if they set not to show again
    let dontAsk = GetLocalSetting("ask_restore_object") === false;
    Log("dont ask: ", dontAsk);
    if (dontAsk === false) setRestoreModalActive(true);
    else RestoreObject();
  }

  //api actions

  /**
   * Moves the current object and all its children to the trash bin
   */
  async function MoveToTrash() {
    showloader();
    props.onCloseModal();
    setTrashModalActive(false);
    await PATCH("/trash-bin", { objectId: props.id }, user, [{ key: "x-object-token", value: GetObjectToken(props.id) ?? "" }]);
    props.forceUpdate();
    hideloader();
  }

  /**
   * Restores the current object and all its children to the trash bin
   */
  async function RestoreObject() {
    showloader();
    let response = await PATCH("/restore-objects", { objects: [props.id] }, user, [{ key: "x-object-token", value: GetObjectToken(props.id) ?? "" }]);
    hideloader();
    if (response.success) {
      props.forceUpdate();
    } else {
      if (response.error.message) {
        Alert(globalAlert, "error", "There was an error restoring one or more objects", response.error.message);
      } else {
        Alert(globalAlert, "error", "Something Went Wrong", "unkown error occurred try again later");
      }
    }
  }

  /**
   * submits a requerst to permenantly delete the current object
   */
  async function DeleteObjectPermenantly() {
    showloader();
    await DELETE("/delete-object", { objectId: props.id }, user, [{ key: "x-object-token", value: GetObjectToken(props.id) ?? "" }]);
    props.forceUpdate();
    hideloader();
  }

  const password_validation = (e, set_err) => {
    let value = e.target.value;
    let message = undefined;

    if (value.length < 8) {
      message = "Password must be at least 8 characters long";
    } else if (!/[A-Z]/.test(value)) {
      message = "Password must contain at least one uppercase letter";
    } else if (!/[a-z]/.test(value)) {
      message = "Password must contain at least one lowercase letter";
    } else if (!/[0-9]/.test(value)) {
      message = "Password must contain at least one number";
    }

    set_err(message);
    setPasswordFieldsValid(message === undefined);
  };

  const confirm_password_validation = (e, set_err) => {
    //use common logic
    let message = password_validation(e, set_err);

    if (passwordInput.current.value !== confirmPasswordInput.current.value) {
      message = "Passwords do not match";
    }

    set_err(message);
    setPasswordFieldsValid(message === undefined);
  };

  function GetPasswordValidationMessage() {
    if (passwordInput.current) {
      let value = passwordInput.current.value;
      if (!value) return "Required";
      else {
        return passwordValidation;
      }
    } else if (settingIsLocked) {
      return "Required";
    }
  }

  function GetConfirmPasswordValidationMessage() {
    if (confirmPasswordInput.current) {
      let value = confirmPasswordInput.current.value;
      if (!value) return "Required";
      else {
        return confirmPasswordValidation;
      }
    } else if (settingIsLocked) {
      return "Required";
    }
  }

  function ValidateEditForm() {
    let isValid = true;

    //user is setting a non locked object to locked
    if (!props.locked && isLockedInput.current.checked === true) {
      //confirm they set a password, and validate that password
      if (!passwordFieldsValid) isValid = false;
    }
    return isValid;
  }

  async function EditObject() {
    if (!ValidateEditForm()) return;

    showloader();

    let payload = {
      objectId: props.id,
      objectName: name_input.current.value,
      move_object: {
        to: base_dir_input.current.value,
        from: props.directory,
      },
      public: isPublicInput.current.checked,
      hidden: isHiddenInput.current.checked,
      locked: isLockedInput.current.checked,
      password: (passwordInput.current && passwordInput.current.value) ?? undefined,
    };
    let response = await PATCH("/edit-object", payload, user, [{ key: "x-object-token", value: GetObjectToken(props.id) ?? "" }]);
    hideloader();

    if (response.updatePropertiesResponse && response.updatePropertiesResponse.success) {
      Alert(globalAlert, "success", "Update Success", "your changes have been saved");
    } else {
      if (response.updatePropertiesResponse.error) {
        Alert(globalAlert, "error", "Update Failed", response.updatePropertiesResponse.error.message);
      } else {
        Alert(globalAlert, "error", "Update Failed", "an unkown error occurred");
      }
    }

    props.forceUpdate();
  }

  function SetHeaderText() {
    if (props.isPublicView) {
      if (props.isFolder) return "Folder Details";
      else return "File Details";
    }

    if (!props.isTrashBin) {
      if (props.isFolder) return "Edit Folder";
      else return "Edit File";
    } else {
      if (props.isFolder) return "Manage Folder";
      else return "Manage File";
    }
  }

  function RenderPublicUrl() {
    if (props.public && user) {
      const publicUrl = `${window.location.origin.toString()}/public/${user.id}/${props.id}`;
      const downloadUrl = `${API_URL}/get-file/${props.id}`;
      return (
        <>
          {/* Publicn View URL */}
          <div className="input-box">
            <label className="input-label" htmlFor="username-input">
              Public View Url
            </label>
            <div className="copy-input-container">
              <input type="text" placeholder="" value={publicUrl} />
              <button
                title="copy to clipboard"
                className="copy-url-button"
                onClick={() => {
                  try {
                    navigator.clipboard
                      .writeText(publicUrl)
                      .then(() => {
                        Log("Copied to clipboard:", publicUrl);
                        pushNotification("Copied to Clipboard", publicUrl);
                      })
                      .catch((err) => {
                        document.execCommand("copy", publicUrl);
                      });
                  } catch (error) {
                    fallbackCopy(publicUrl);
                    pushNotification("Copied to Clipboard", publicUrl);
                  }
                }}
              >
                <img src={copy} alt="" />
              </button>
            </div>
          </div>

          {/* Public Download URL */}
          {props.isFolder === false && (
            <div className="input-box">
              <label className="input-label" htmlFor="username-input">
                Direct Download Url
              </label>
              <div className="copy-input-container">
                <input type="text" placeholder="" value={downloadUrl} />
                <button
                  title="copy to clipboard"
                  className="copy-url-button"
                  onClick={() => {
                    try {
                      navigator.clipboard
                        .writeText(downloadUrl)
                        .then(() => {
                          Log("Copied to clipboard:", downloadUrl);
                          pushNotification("Copied to Clipboard", downloadUrl);
                        })
                        .catch((err) => {
                          document.execCommand("copy", downloadUrl);
                        });
                    } catch (error) {
                      fallbackCopy(downloadUrl);
                      pushNotification("Copied to Clipboard", downloadUrl);
                    }
                  }}
                >
                  <img src={copy} alt="" />
                </button>
              </div>
            </div>
          )}

          <ModalDividerLine showDivider={showDividerLine} />
        </>
      );
    }
  }

  function GetModalConfig() {
    let config = {
      id: "edit-object-modal-" + props.id,
      class: "edit-object-modal",
      animate: true,
      showDividerLine,
      textAlign: "center",
      header: SetHeaderText(),
      isActive: props.isActive,
      onClickOut: props.onClickOut,
    };

    if (!props.isPublicView) {
      config.icon = trashIcon;
      config.iconHeight = "20px";
      config.onIconClick = OnTrashClicked;
      config.iconAsButton = true;
    }

    return config;
  }

  function RenderModalContent() {
    //objects not in the trash bin
    if (props.isPublicView) {
      return (
        <section className="modal-controls-section">
          <div className="edit-object-modal-controls">
            <button onClick={props.onCloseModal} className="standard-button close-button">
              Close
            </button>
          </div>
        </section>
      );
    }

    if (!props.isTrashBin) {
      return (
        <>
          <div className="input-box">
            <label className="input-label" htmlFor="username-input">
              {modal_type} Name
            </label>
            <input
              ref={name_input}
              type="text"
              id={props.id + "-name-input"}
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <ModalDividerLine showDivider={showDividerLine} />

          <div className="input-box">
            <label className="input-label" htmlFor="username-input">
              Move {modal_type}
            </label>
            <select ref={base_dir_input} id={props.id + "-dir-input"} defaultValue={props.directory} data-value={props.directory}>
              <option value="">Select</option>
              {RenderPossibleDirectories()}
            </select>
          </div>

          <ModalDividerLine showDivider={showDividerLine} />

          <div className="input-box-toggle">
            <label className="input-label" htmlFor="username-input">
              Public
            </label>
            <input type="checkbox" ref={isPublicInput} id={props.id + "-public-input"} placeholder="" defaultChecked={props.public ?? false} />
          </div>

          <ModalDividerLine showDivider={showDividerLine} />

          <div className="input-box-toggle">
            <label className="input-label" htmlFor="username-input">
              Hidden
            </label>
            <input type="checkbox" ref={isHiddenInput} id={props.id + "-hidden-input"} placeholder="" defaultChecked={props.hidden} />
          </div>

          <ModalDividerLine showDivider={showDividerLine} />

          <div className="input-box-toggle">
            <label className="input-label" htmlFor="username-input">
              Locked
            </label>
            <input
              type="checkbox"
              onChange={(e) => {
                let value = e.target.checked;
                if (!props.locked && value) setSettingIsLocked(true);
                if (!props.locked && !value) setSettingIsLocked(false);
                if (props.locked) setSettingIsLocked(false);
              }}
              ref={isLockedInput}
              id={props.id + "-lock-input"}
              defaultChecked={props.locked}
            />
          </div>

          {/* Render Password Input */}
          {settingIsLocked ? (
            <>
              {/* Password */}
              <div className="input-box">
                <label className="input-label" htmlFor="password-input">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Object Password"
                  id={props.id + "-password-input"}
                  onChange={(e) => password_validation(e, setPasswordValidation)}
                  ref={passwordInput}
                />
                <p className="validation-message">{GetPasswordValidationMessage()}</p>
              </div>

              {/* Confirm Password */}
              <div className="input-box">
                <label className="input-label" htmlFor="confirm-password-input">
                  Confirm Password
                </label>
                <input
                  type="password"
                  placeholder="Confirm Object Password"
                  id={props.id + "-confirm-password-input"}
                  onChange={(e) => confirm_password_validation(e, setConfirmPasswordValidation)}
                  ref={confirmPasswordInput}
                />
                <p className="validation-message">{GetConfirmPasswordValidationMessage()}</p>
              </div>
            </>
          ) : (
            <></>
          )}

          <ModalDividerLine showDivider={showDividerLine} />

          {RenderPublicUrl()}

          <section className="modal-controls-section">
            <div className="edit-object-modal-controls">
              <button onClick={props.onCloseModal} className="standard-button close-button">
                Close
              </button>
              <button className="standard-button" onClick={EditObject}>
                Save Changes
              </button>
            </div>
          </section>
        </>
      );
    } else {
      //trash bin objects
      return (
        <>
          <section className="modal-controls-section">
            {/* vertical spacer */}
            <div className="spacer-v-l"></div>

            <div className="edit-object-modal-controls">
              <button onClick={props.onCloseModal} className="standard-button close-button">
                Close
              </button>
              <button onClick={OnRestoreClicked} className="standard-button btn-green close-button">
                Restore
              </button>
            </div>
          </section>
        </>
      );
    }
  }

  return (
    <Modal {...GetModalConfig()}>
      {/* Move To Trash Modal */}
      {!props.isTrashBin && (
        <ConfirmationModal
          isActive={trashModalActive}
          includeDontAskAgain={true}
          dontAskSetting={"ask_move_to_trash"}
          animate={true}
          id={"trash-bin-modal-" + props.id}
          header={"Are You Sure?"}
          message={`"${props.objectName}" will be moved to the Trash bin`}
          onDeny={() => setTrashModalActive(false)}
          onConfirm={MoveToTrash}
          confirmText="Yes"
          denyText="No"
        />
      )}

      {/* Modals for Trash Bin UI */}
      {props.isTrashBin && (
        <>
          {/* Permenantly Delete Modal */}
          <ConfirmationModal
            iconAsButton={false}
            icon={wanrningIcon}
            isActive={trashModalActive}
            includeDontAskAgain={true}
            dontAskSetting={"ask_permenantly_delete"}
            animate={true}
            id={"trash-bin-modal-" + props.id}
            header={"Warning"}
            message={`"${props.objectName}" will be permenantly deleted!`}
            onDeny={() => setTrashModalActive(false)}
            onConfirm={DeleteObjectPermenantly}
            confirmText="Delete Forever"
            denyText="Cancel"
          />

          {/* Restore Object Modal */}
          <ConfirmationModal
            isActive={restoreModalActive}
            includeDontAskAgain={true}
            dontAskSetting={"ask_restore_object"}
            animate={true}
            id={"restore-object-modal-" + props.id}
            header={"Are You Sure?"}
            message={`"${props.objectName}" will be restored to its original directory`}
            onDeny={() => setRestoreModalActive(false)}
            onConfirm={RestoreObject}
            confirmClass="btn-green"
            confirmText="Restore"
            denyText="Cancel"
          />
        </>
      )}

      <div className="meta-data">
        <div className="meta-data-value">
          <label htmlFor="">Date Created: </label>
          <span className="meta-data-val">{new Date(props.DateCreated).toLocaleString()}</span>
        </div>
        <div className="meta-data-value">
          <label htmlFor="">Last Modified:</label>
          <span className="meta-data-val">{new Date(props.DateModified).toLocaleString()}</span>
        </div>
        <div className="meta-data-value">
          <label htmlFor="">Size:</label>
          <span className="meta-data-val">{formatBytes(props.bytes)}</span>
        </div>
      </div>

      <ModalDividerLine showDivider={showDividerLine} />

      {/* Modal Content */}
      {RenderModalContent()}
    </Modal>
  );
}

export default EditObjectModal;
